import { contrastRatio, hexToHslParts, hslToHex, parseHex6, readableAccentHex } from '@/lib/color-utils';

/**
 * A seller's solid page background (`design.backgroundColor`), and the palette
 * a page painted in it uses.
 *
 * THE COLOUR IS PAINTED EXACTLY AS PICKED. Unlike the accent, which is darkened
 * until its button labels read (clampAccentHex), a background is the one colour
 * a seller sees most of, so it is never moved. Everything drawn ON it moves
 * instead:
 *
 *   - The page's light or dark mode follows the colour (`pageBackgroundTheme`):
 *     dark text on a light colour, light text on a dark one, whichever reads
 *     better. Pure black or white text clears 4.5:1 on any colour at all (the
 *     worst case is a mid-tone, where both sit near 4.6), so there is no
 *     colour a seller can pick that leaves the page unreadable. The theme's own
 *     softer ink is used where it also clears 4.5:1, which is almost everywhere.
 *   - Cards, panels, borders and inputs become shades of the same colour, a
 *     step away from it in lightness, the same steps globals.css puts between
 *     the stock page and its panels. A panel that carries text takes its step
 *     in whichever direction keeps the text readable, so on a mid-tone a light
 *     page's cards step lighter instead of darker.
 *   - Secondary text is the main text blended towards the page as far as it can
 *     go while still clearing 4.5:1 on every surface it can sit on.
 *   - The status colours (errors, success, warnings, info, rating stars) keep
 *     their hue and move in lightness only as far as they must to clear 4.5:1
 *     on every surface, so a red error stays readable on a red page. Where one
 *     moved, the text drawn ON it (its `-foreground`) is re-picked too.
 *
 * The accent is the one colour not handled here: it is the seller's own pick,
 * clamped for its button labels (clampAccentHex) and turned into readable ink
 * against these surfaces by ThemeProvider (readableAccentHex's `surfaces`).
 *
 * So a seller's design has no separate light or dark choice while it has a
 * background colour: the colour decides (resolveDesignOverride in
 * src/lib/product-design.ts), and the editors hide the theme picker then.
 *
 * Every colour here is a hex first and becomes an HSL triplet (the format
 * globals.css's tokens use) only at the end, from that same hex, so the
 * contrast this module checks is the contrast the page renders.
 *
 * Pure: the node test runner loads it (src/lib/page-background.test.ts).
 */

export type PageBackgroundTheme = 'light' | 'dark';

/** WCAG AA for normal-sized text. */
const MIN_TEXT_CONTRAST = 4.5;

/** The stock `--foreground` of each theme (globals.css), as hex. */
const THEME_INK: Record<PageBackgroundTheme, string> = { light: '#09090b', dark: '#fafafa' };
/** Pure ink, for the narrow band of mid-tones where the stock ink falls just short. */
const PURE_INK: Record<PageBackgroundTheme, string> = { light: '#000000', dark: '#ffffff' };

/** The panels a page draws over its background, in globals.css's token names. */
type Surface = 'card' | 'popover' | 'secondary' | 'muted' | 'accent' | 'border' | 'input';

/**
 * How far each panel sits from the page in globals.css, in HSL lightness
 * points, signed the way the stock theme steps: the light page draws its cards
 * darker (96% page, 91% card), the dark one lighter (3% page, 8% card).
 * Keep in step with globals.css.
 */
const SURFACE_STEPS: Record<PageBackgroundTheme, Record<Surface, number>> = {
  light: { card: -5, popover: 1, secondary: -11, muted: 0, accent: 0, border: -11, input: -24 },
  dark: { card: 5, popover: 5, secondary: 8, muted: 9, accent: 9, border: 19, input: 27 },
};

/** The panels text is drawn on. Borders and input outlines carry none. */
const TEXT_SURFACES: readonly Surface[] = ['card', 'popover', 'secondary', 'muted', 'accent'];

/** The status colours, which are used as text (an error line) and as surfaces (a destructive button). */
type Status = 'destructive' | 'success' | 'warning' | 'info' | 'star';
type HslTuple = readonly [number, number, number];

/**
 * Each theme's stock status colours and the ink drawn on them, exactly as
 * globals.css declares them. `star` is never a surface, so it has no ink.
 * Keep in step with globals.css.
 */
const STATUS_STOCK: Record<PageBackgroundTheme, Record<Status, { colour: HslTuple; ink?: HslTuple }>> = {
  light: {
    destructive: { colour: [0, 72, 41], ink: [0, 0, 98] },
    success: { colour: [142, 72, 24], ink: [0, 0, 98] },
    warning: { colour: [38, 92, 26], ink: [0, 0, 98] },
    info: { colour: [199, 89, 29], ink: [0, 0, 98] },
    star: { colour: [45, 100, 50] },
  },
  dark: {
    destructive: { colour: [0, 84, 64], ink: [240, 10, 3.9] },
    success: { colour: [142, 70, 45], ink: [240, 10, 3.9] },
    warning: { colour: [38, 92, 55], ink: [240, 10, 3.9] },
    info: { colour: [199, 89, 55], ink: [240, 10, 3.9] },
    star: { colour: [45, 100, 55] },
  },
};

const STATUS_ORDER: readonly Status[] = ['destructive', 'success', 'warning', 'info', 'star'];

/**
 * Every custom property a palette sets on `<html>`, so ThemeProvider can take
 * each one off again when the page closes. All of them are tokens globals.css
 * defines for both themes, so removing the inline value hands the page back to
 * the stylesheet.
 */
export const PAGE_BACKGROUND_VARS = [
  '--background',
  '--foreground',
  '--card',
  '--card-foreground',
  '--popover',
  '--popover-foreground',
  '--secondary',
  '--secondary-foreground',
  '--muted',
  '--muted-foreground',
  '--accent',
  '--accent-foreground',
  '--border',
  '--input',
  '--destructive',
  '--destructive-foreground',
  '--success',
  '--success-foreground',
  '--warning',
  '--warning-foreground',
  '--info',
  '--info-foreground',
  '--star',
] as const;
export type PageBackgroundVar = (typeof PAGE_BACKGROUND_VARS)[number];

export type PageBackgroundPalette = {
  /** Light or dark: the page is painted in this theme while the colour is on. */
  theme: PageBackgroundTheme;
  /** Each token as an HSL triplet ("217.2 91.2% 59.8%"), the form globals.css uses. */
  vars: Record<PageBackgroundVar, string>;
  /**
   * The page and every panel text sits on, as hex: what accent-coloured text
   * has to clear (readableAccentHex's `surfaces`).
   */
  textSurfaces: string[];
};

/**
 * Light or dark for a page painted in `hex`: the mode whose text reads better
 * on it. A light colour gets dark text (the light theme), a dark colour light
 * text. The crossover is a relative luminance of about 0.18, a mid-tone.
 */
export function pageBackgroundTheme(hex: string): PageBackgroundTheme {
  return contrastRatio(PURE_INK.light, hex) >= contrastRatio(PURE_INK.dark, hex) ? 'light' : 'dark';
}

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value));

/** "h s% l%" for a hex, unrounded beyond two places, so it renders as that hex. */
function triplet(hex: string): string {
  const [h, s, l] = hexToHslParts(hex);
  const fmt = (n: number) => String(Number(n.toFixed(2)));
  return `${fmt(h)} ${fmt(s)}% ${fmt(l)}%`;
}

/** `a` blended `t` of the way towards `b`, per sRGB channel. */
function mixHex(a: string, b: string, t: number): string {
  const channel = (hex: string, i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  let out = '#';
  for (let i = 0; i < 3; i++) {
    const v = Math.round(channel(a, i) + (channel(b, i) - channel(a, i)) * t);
    out += v.toString(16).padStart(2, '0');
  }
  return out;
}

const worstContrast = (ink: string, surfaces: readonly string[]): number =>
  Math.min(...surfaces.map((surface) => contrastRatio(ink, surface)));

/** A stock HSL tuple in globals.css's own notation, so an unmoved token is byte-identical to the stylesheet's. */
const hslTriplet = ([h, s, l]: HslTuple): string => `${h} ${s}% ${l}%`;

/** The text to draw on `surface`: the stock ink that reads better on it, or pure black or white where neither clears 4.5:1. */
function bestInk(surface: string): string {
  const light = contrastRatio(THEME_INK.light, surface);
  const dark = contrastRatio(THEME_INK.dark, surface);
  const pick = light >= dark ? 'light' : 'dark';
  return Math.max(light, dark) >= MIN_TEXT_CONTRAST ? THEME_INK[pick] : PURE_INK[pick];
}

type StatusVar = Extract<PageBackgroundVar, `--${Status}` | `--${Status}-foreground`>;

/**
 * The palette as one stylesheet rule, which a seller's page renders into its
 * own HTML (SellerPageBackground) so the colour is there on the first paint.
 * ThemeProvider writes the same tokens inline on <html> once the page has
 * hydrated, which is what previews and the rest of the shell read; this rule
 * only covers the moment before that, and leaves with the page.
 *
 * `:root:root` outranks both theme blocks in globals.css (`:root`, `.dark`), so
 * the colours hold whichever class the visitor's own theme put on <html>; the
 * inline tokens outrank it in turn. `color-scheme` gives scrollbars and form
 * controls the matching mode. Every value is a number triplet built here from
 * a parsed hex, so nothing a seller typed reaches the stylesheet as text.
 */
export function pageBackgroundCss(hex: unknown): string | null {
  const palette = pageBackgroundPalette(hex);
  if (!palette) return null;
  const declarations = Object.entries(palette.vars).map(([name, value]) => `${name}:${value}`).join(';');
  return `:root:root{${declarations};color-scheme:${palette.theme}}`;
}

/**
 * The palette for a page painted in `hex`, or null for anything that is not a
 * six-digit hex (a stored value is untrusted until it parses).
 */
export function pageBackgroundPalette(hex: unknown): PageBackgroundPalette | null {
  const background = parseHex6(hex);
  if (!background) return null;

  const theme = pageBackgroundTheme(background);
  const ink = contrastRatio(THEME_INK[theme], background) >= MIN_TEXT_CONTRAST ? THEME_INK[theme] : PURE_INK[theme];

  // Panels keep the page's hue and saturation and move in lightness only, so
  // they read as the same colour. A panel that carries text steps the theme's
  // way unless that costs the text its contrast, then the other way, which
  // only ever adds contrast (it moves away from the ink).
  const [h, s, l] = hexToHslParts(background);
  const surfaces = {} as Record<Surface, string>;
  for (const surface of Object.keys(SURFACE_STEPS[theme]) as Surface[]) {
    const step = SURFACE_STEPS[theme][surface];
    let shade = hslToHex(h, s, clampPercent(l + step));
    if (TEXT_SURFACES.includes(surface) && contrastRatio(ink, shade) < MIN_TEXT_CONTRAST) {
      shade = hslToHex(h, s, clampPercent(l - step));
      if (contrastRatio(ink, shade) < MIN_TEXT_CONTRAST) shade = background;
    }
    surfaces[surface] = shade;
  }

  const textSurfaces = [background, ...TEXT_SURFACES.map((surface) => surfaces[surface])];

  // Secondary text: the ink blended towards the page a step at a time, as far
  // as it goes while it clears 4.5:1 on every surface it can land on. On a
  // high-contrast page that is a soft grey in the page's own tint; on a
  // mid-tone, where there is little contrast to spare, it stays close to the
  // main text.
  let muted = ink;
  for (let i = 1; i <= 100; i++) {
    const candidate = mixHex(ink, background, i / 100);
    if (worstContrast(candidate, textSurfaces) < MIN_TEXT_CONTRAST) break;
    muted = candidate;
  }

  // The status colours: the theme's own where it already reads on every
  // surface, so most pages keep the exact stock red and green; otherwise the
  // same hue walked lighter (dark page) or darker (light page) until it does,
  // with the ink on it re-picked for its new lightness.
  const status = {} as Record<string, string>;
  for (const name of STATUS_ORDER) {
    const stock = STATUS_STOCK[theme][name];
    const stockHex = hslToHex(...stock.colour);
    const readable = readableAccentHex(stockHex, theme === 'dark', textSurfaces) ?? stockHex;
    const moved = readable !== stockHex;
    status[`--${name}`] = moved ? triplet(readable) : hslTriplet(stock.colour);
    if (stock.ink) status[`--${name}-foreground`] = moved ? triplet(bestInk(readable)) : hslTriplet(stock.ink);
  }

  return {
    theme,
    vars: {
      ...(status as Record<StatusVar, string>),
      '--background': triplet(background),
      '--foreground': triplet(ink),
      '--card': triplet(surfaces.card),
      '--card-foreground': triplet(ink),
      '--popover': triplet(surfaces.popover),
      '--popover-foreground': triplet(ink),
      '--secondary': triplet(surfaces.secondary),
      '--secondary-foreground': triplet(ink),
      '--muted': triplet(surfaces.muted),
      '--muted-foreground': triplet(muted),
      '--accent': triplet(surfaces.accent),
      '--accent-foreground': triplet(ink),
      '--border': triplet(surfaces.border),
      '--input': triplet(surfaces.input),
    },
    textSurfaces,
  };
}
